import classNames from 'classnames';
import { CSSProperties, FunctionComponent, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import BreadCrumbs from './components/atoms/BreadCrumbs';
import { showNotification } from './components/molecules/Notifications';
import AuthStep from './components/organisms/AuthStep';
import PaymentStep from './components/organisms/PaymentStep';
import ShippingStep from './components/organisms/ShippingStep';
import { useCheckoutState } from './utils/checkoutState';
import { config } from './utils/config';
import getConfigNotifications from './utils/getConfigNotifications';
import { getInitialRoute, getStepFromRoute } from './utils/routing';
import { pushInitialTrackingEvents, pushTrackingEvent } from './utils/tracking';
import useStickyScrolling from './utils/useStickyScrolling';

let Basket: FunctionComponent<{ desktopStickyStyle?: CSSProperties }> | undefined;

if (HAS_BASKET) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
  Basket = require('./components/organisms/Basket').default;
}

let Header: FunctionComponent | undefined;
let Footer: FunctionComponent | undefined;

if (!IS_WEB_COMPONENT) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
  Header = require('./components/atoms/Header').default;
  // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports
  Footer = require('./components/atoms/Footer').default;
}

const App = () => {
  const { contentRef, contentStyles, basketStyles } = useStickyScrolling();
  const { checkoutState } = useCheckoutState();
  const { formatMessage } = useIntl();

  useEffect(() => {
    pushInitialTrackingEvents(checkoutState);
  }, []);

  const location = useLocation();
  const previousPathnameRef = useRef<string>();
  useEffect(() => {
    if (location.pathname === '/' || location.pathname === previousPathnameRef.current) {
      return;
    }

    previousPathnameRef.current = location.pathname;

    window.scrollTo({ top: 0, behavior: 'smooth' });

    const step = getStepFromRoute(location.pathname);

    pushTrackingEvent({
      event: 'content_view',
      content_name: window.location.pathname + window.location.hash,
      search: window.location.search || null,
      app_version: 'v3',
      appId: config.appId,
      page_type: 'checkout:' + step,
    });

    (async () => {
      const configNotifications = await getConfigNotifications(step);
      configNotifications.forEach(notification => {
        showNotification({
          level: notification.type ?? 'info',
          text: formatMessage({ id: notification.textTranslationKey }),
          autoClose: true,
        });
      });
    })();
  }, [location]);

  const isBreadcrumbsEnabled = false;

  return (
    <>
      <div className="show-mobile">
        {Header && <Header />}
        {isBreadcrumbsEnabled && <BreadCrumbs />}
      </div>
      <div className={classNames({ main: HAS_BASKET })}>
        <div className="content-container" ref={contentRef} style={contentStyles}>
          <div className="content-container__max-width">
            <div className="show-desktop">
              {Header && <Header />}
              {isBreadcrumbsEnabled && <BreadCrumbs />}
            </div>
            <div className="form-container">
              <Routes>
                <Route path="/" element={<Navigate to={getInitialRoute(checkoutState)} replace />} />
                <Route path="/auth" element={<AuthStep />} />
                <Route path="/shipping" element={<ShippingStep />} />
                <Route path="/payment" element={<PaymentStep />} />
              </Routes>
            </div>
          </div>
        </div>
        {Basket && <Basket desktopStickyStyle={basketStyles} />}
      </div>
      {Footer && <Footer />}
    </>
  );
};

export default App;
